<template>
    <div class="words-graph" ref="wrapRef">
        <div class="words-graph__head">相关主题词</div>

        <div class="words-graph__toolbar">
            <div class="words-graph__filters">
                <el-row class="words-graph__year-range">
                    <el-col :span="16">
                        <el-date-picker
                            v-model="yearStart"
                            type="year"
                            value-format="yyyy"
                            format="yyyy年"
                            placeholder="起始年"
                            :clearable="false"
                            size="small"
                            class="words-graph__year-picker"
                        />
                        <span class="words-graph__year-sep">-</span>
                        <el-date-picker
                            v-model="yearEnd"
                            type="year"
                            value-format="yyyy"
                            format="yyyy年"
                            placeholder="结束年"
                            :clearable="false"
                            size="small"
                            class="words-graph__year-picker"
                        />
                    </el-col>
                    <el-col :span="8">
                        <span class="words-graph__top-label">词频TOP:</span>
                        <el-input-number
                            v-model="topCount"
                            :min="1"
                            :max="50"
                            :controls="false"
                            size="small"
                            class="words-graph__top-input"
                        />
                    </el-col> 
                    
                </el-row>
                <div class="top-btn">
                    <el-button type="primary" size="small" class="words-graph__submit" @click="onSubmit">
                        提交
                    </el-button>
                    <el-button type="success" size="small" class="words-graph__fullscreen" @click="openFullscreen">
                        <i class="el-icon-full-screen"></i> 全屏展示
                    </el-button>
                </div> 
            </div>
        

            <!-- ECharts 图谱挂载容器（this.$echarts.init） -->
            <div ref="chartRef" class="words-graph__canvas"></div>

            <div class="words-graph__summary">
                图解：取分析词相关的发文文献，选取词频最高的前{{ topCount }}个关键词，再分别以这{{ topCount }}个关键词关联出二级关键词，以此分析词与词之间的关联关系。
            </div>
        </div>
        <el-drawer
            title="相关主题词"
            :visible.sync="fullscreenVisible"
            size="100%" 
            append-to-body
            custom-class="words-graph-dialog"
            @opened="renderFullscreenChart"
            @closed="destroyFullscreenChart"
        >
    
            <!-- ECharts 全屏图谱挂载容器 -->
            <div ref="fullscreenChartRef" class="words-graph__canvas words-graph__canvas--fullscreen"></div>
        </el-drawer>
    </div>
</template>

<script>
/**
 * WordsGraph — 相关主题词关系图谱
 *
 * 渲染插件：Apache ECharts（^5.4.1，见 package.json）
 * 全局挂载：main.js → Vue.prototype.$echarts
 * 图表类型：series.type = 'graph' + layout = 'force'（力导向关系图）
 * 数据来源：./graph.js（dataList 节点 / dataListLinks 边 / dataType 分类）
 */
import graphRaw from './graph';

const SUMMARY_TOP = 5;

export default {
    name: 'WordsGraph',
    props: {
        /** 接口返回图谱数据，结构与 graph.js 一致 */
        graphData: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            /** 年份范围：起始年 / 结束年（仅年份，无日期时间） */
            yearStart: '',
            yearEnd: '',
            topCount: SUMMARY_TOP,
            /** ECharts 实例：主画布 */
            chart: null,
            /** ECharts 实例：全屏弹窗画布 */
            fullscreenChart: null,
            fullscreenVisible: false,
            innerData: null,
        };
    },
    computed: {
        sourceData() {
            return this.graphData && this.graphData.data
                ? this.graphData.data
                : graphRaw.data;
        },
    },
    watch: {
        graphData: {
            handler() {
                this.applySourceData();
            },
            deep: true,
        },
    },
    mounted() {
        this.applySourceData();
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        this.destroyChart();
        this.destroyFullscreenChart();
    },
    methods: {
        applySourceData() {
            this.innerData = { ...this.sourceData };
            const startYear = this.innerData.startTime || '1990';
            const endYear = this.innerData.endTime || '2026';
            this.yearStart = String(startYear);
            this.yearEnd = String(endYear);
            this.topCount = this.innerData.glyjcs || SUMMARY_TOP;
            this.$nextTick(() => {
                this.initChart();
                this.renderChart();
            });
        },
        /** 将 graph.js 字符串字段解析为 ECharts graph 所需的 nodes / links / categories */
        parseGraphPayload(raw) {
            const nodes = JSON.parse(raw.dataList || '[]').map((node) => ({
                ...node,
                id: node.name,
                label: {
                    show: true,
                    color: (node.label && node.label.color) || '#333',
                    fontSize: node.symbolSize >= 60 ? 12 : 10,
                },
            }));
            const links = JSON.parse(raw.dataListLinks || '[]').map((link) => ({
                ...link,
                // 
                lineStyle: {
                    color: 'rgba(57, 109, 231, 0.35)',
                    width: 1,
                    curveness: 0.22,
                },
                /** 鼠标移入节点：相邻边加粗，非相邻元素变淡 */
                emphasis: {
                    lineStyle: {
                        width: 4,
                        color: 'rgba(57, 109, 231, 0.9)',
                        curveness: 0.22,
                    },
                },
            }));
            const categories = JSON.parse(raw.dataType || '[]').map((item) => ({
                name: item.name,
            }));
            return { nodes, links, categories };
        },
        /** 组装 ECharts option：graph 力导向关系图 */
        buildOption(raw) {
            const { nodes, links, categories } = this.parseGraphPayload(raw);
            return {
                tooltip: {
                    trigger: 'item',
                    formatter(params) {
                        if (params.dataType === 'edge') {
                            return `${params.data.source} → ${params.data.target}`;
                        }
                        return params.name || '';
                    },
                },
                series: [{
                    type: 'graph', // ECharts 关系图
                    layout: 'force', // 力导向布局（force 算法）
                    roam: true, // 缩放、平移
                    draggable: true, // 节点可拖拽
                    categories,
                    data: nodes,
                    links,
                    symbol: 'circle',
                    label: {
                        show: true,
                        position: 'inside',
                    },
                    lineStyle: {
                        color: 'rgba(57, 109, 231, 0.35)',
                        width: 1,
                        curveness: 0.22,
                    },
                    // 鼠标移入节点：相邻边加粗，非相邻元素变淡
                    emphasis: {
                        focus: 'adjacency',
                        scale: 1.08,
                        itemStyle: {
                            shadowBlur: 12,
                            shadowColor: 'rgba(57, 109, 231, 0.45)',
                        },
                        lineStyle: {
                            width: 4,
                            color: 'rgba(57, 109, 231, 0.9)',
                            curveness: 0.22,
                        },
                    },
                    blur: {
                        itemStyle: { opacity: 0.25 },
                        lineStyle: { opacity: 0.12, width: 1 },
                    },
                    force: {
                        repulsion: 140, // 节点斥力
                        edgeLength: [50, 110], // 边长范围
                        gravity: 0.12, // 向心力
                    },
                }],
            };
        },
        /** 初始化 ECharts 实例（主画布） */
        initChart() {
            if (this.chart || !this.$refs.chartRef) return;
            this.chart = this.$echarts.init(this.$refs.chartRef);
        },
        /** 渲染 / 更新主画布图谱 */
        renderChart() {
            this.initChart();
            if (!this.chart) return;
            const raw = this.innerData || this.sourceData;
            this.chart.setOption(this.buildOption(raw), true);
        },
        onSubmit() {
            const start = Number(this.yearStart);
            const end = Number(this.yearEnd);
            if (!start || !end || start > end) {
                this.$message.warning('请选择有效的年份范围');
                return;
            }
            this.renderChart();
        },
        openFullscreen() {
            this.fullscreenVisible = true;
        },
        /** 全屏弹窗打开后，独立初始化第二个 ECharts 实例 */
        renderFullscreenChart() {
            if (!this.$refs.fullscreenChartRef) return;
            if (!this.fullscreenChart) {
                this.fullscreenChart = this.$echarts.init(this.$refs.fullscreenChartRef);
            }
            const raw = this.innerData || this.sourceData;
            this.fullscreenChart.setOption(this.buildOption(raw), true);
            this.fullscreenChart.resize();
        },
        handleResize() {
            if (this.chart) this.chart.resize();
            if (this.fullscreenChart) this.fullscreenChart.resize();
        },
        /** 销毁 ECharts 实例，避免内存泄漏 */
        destroyChart() {
            if (this.chart) {
                this.chart.dispose();
                this.chart = null;
            }
        },
        destroyFullscreenChart() {
            if (this.fullscreenChart) {
                this.fullscreenChart.dispose();
                this.fullscreenChart = null;
            }
        },
    },
};
</script>

<style lang="less" scoped>
.words-graph {
    background: #fff; 
    padding: 20px 0 16px;
    box-sizing: border-box;
    margin-bottom: 20px;

    &__head {
        font-size: 16px;
        font-weight: bold;
        color: #333;
        margin-bottom: 16px;
        padding-left: 10px;
        border-left: 4px solid #396de7;
        line-height: 1.4;
    }

    &__toolbar {
        // display: flex;
        // align-items: flex-start;
        // justify-content: space-between;
        // gap: 8px;
        padding:8px;
        margin-bottom: 12px;
        border: 1px solid #e4e7ed;
    }

    &__filters {
        // display: flex;
        // flex-wrap: wrap;
        // align-items: center;
        gap: 6px;
        flex: 1;
        min-width: 0;
    }

    &__year-range {
        display: flex;
        align-items: center;
        width: 100%;
        margin-bottom: 10px;
    }

    &__year-picker {
        width: 110px;
    }

    &__year-sep {
        margin: 0 6px;
        color: #666;
    }

    &__top-label {
        font-size: 14px;
        color: #333;
        margin-right: 8px;
        white-space: nowrap;
    }

    &__top-input {
        width: 52px;

        ::v-deep .el-input__inner {
            padding: 0 8px;
            text-align: center;
        }
    }
    .top-btn{
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
    }  

    &__canvas {
        width: 100%;
        height: 360px;
        // border: 1px solid #e4e7ed;
        &--fullscreen {
            height: 70vh;
        }
    }

    &__summary {
        margin-top: 12px;
        font-size: 14px;
        line-height: 1.8; 
    }
}
</style>

<style lang="less">
.words-graph-dialog {
    .el-dialog__body {
        padding-top: 10px;
    }
}
</style>
